import { ref, readonly } from "#imports";
export function useChatSessionTimeout() {
  const sessionExpirationDate = ref(null);
  const sessionTimeoutId = ref(null);
  const isSessionActive = ref(false);
  const sessionTimeoutConfig = ref({
    timeoutHours: 2,
    checkIntervalMs: 6e4,
    // 1 minute
    warningThresholdMs: 3e5,
    // 5 minutes warning before expiration
    onSessionExpired: void 0,
    onSessionWarning: void 0
  });
  function updateSessionExpiration() {
    const now = /* @__PURE__ */ new Date();
    const timeoutMs = (sessionTimeoutConfig.value.timeoutHours || 2) * 60 * 60 * 1e3;
    sessionExpirationDate.value = new Date(now.getTime() + timeoutMs);
    console.log("Session expiration updated to:", sessionExpirationDate.value);
  }
  function checkSessionExpiration() {
    if (!sessionExpirationDate.value || !isSessionActive.value) {
      return;
    }
    const now = /* @__PURE__ */ new Date();
    const timeRemaining = sessionExpirationDate.value.getTime() - now.getTime();
    if (timeRemaining <= 0) {
      endSession();
      return;
    }
    const warningThreshold = sessionTimeoutConfig.value.warningThresholdMs || 3e5;
    if (timeRemaining <= warningThreshold && sessionTimeoutConfig.value.onSessionWarning) {
      sessionTimeoutConfig.value.onSessionWarning(timeRemaining);
    }
  }
  function startSessionTimeoutChecker() {
    if (sessionTimeoutId.value) {
      clearInterval(sessionTimeoutId.value);
    }
    if (import.meta.server) {
      return;
    }
    const checkInterval = sessionTimeoutConfig.value.checkIntervalMs ?? 6e4;
    sessionTimeoutId.value = setInterval(() => {
      checkSessionExpiration();
    }, checkInterval);
  }
  function stopSessionTimeoutChecker() {
    if (sessionTimeoutId.value) {
      clearInterval(sessionTimeoutId.value);
      sessionTimeoutId.value = null;
    }
  }
  function startSession() {
    isSessionActive.value = true;
    updateSessionExpiration();
    startSessionTimeoutChecker();
  }
  function endSession() {
    if (!isSessionActive.value) {
      return;
    }
    isSessionActive.value = false;
    stopSessionTimeoutChecker();
    sessionExpirationDate.value = null;
    sessionTimeoutConfig.value.onSessionExpired?.();
  }
  function resetSessionTimeout() {
    if (isSessionActive.value) {
      updateSessionExpiration();
    }
  }
  function configureSessionTimeout(config) {
    sessionTimeoutConfig.value = {
      ...sessionTimeoutConfig.value,
      ...config
    };
    if (isSessionActive.value) {
      startSessionTimeoutChecker();
    }
  }
  function getSessionTimeRemaining() {
    if (!sessionExpirationDate.value) {
      return null;
    }
    const now = /* @__PURE__ */ new Date();
    const remaining = sessionExpirationDate.value.getTime() - now.getTime();
    return Math.max(0, remaining);
  }
  function getSessionExpirationDate() {
    return sessionExpirationDate.value;
  }
  function isSessionExpired() {
    if (!sessionExpirationDate.value) {
      return false;
    }
    const now = /* @__PURE__ */ new Date();
    return now >= sessionExpirationDate.value;
  }
  return {
    sessionExpirationDate: readonly(sessionExpirationDate),
    isSessionActive: readonly(isSessionActive),
    startSession,
    endSession,
    resetSessionTimeout,
    configureSessionTimeout,
    getSessionTimeRemaining,
    getSessionExpirationDate,
    isSessionExpired
  };
}
